import { useState } from 'react';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { icons } from '@assets/index';
import { Input } from '@components/common';
import { InputSearch } from '@components/common/InputSearch/InputSearch';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import FormField from '@modules/form/components/FormField';

import { isPersonalDataNationalInfoRequired } from '../../const/SubscriberIdPersonalDataEdit';
import { personalDataTranslationSelector } from '../../selectors';
import { useSearchPersonalDataNationalityQuery } from '../../store';
import type { IPersonalDataInfoEdit } from '../../types/info-edit';
import type { IPersonalDataDocType } from '../../types/meta';

interface IPersonalDataEditNationality {
  docTypeSelected?: IPersonalDataDocType;
}

const PersonalDataEditNationality = ({
  docTypeSelected,
}: IPersonalDataEditNationality) => {
  const { setFieldValue, setFieldTouched, validateField } =
    useFormikContext<IPersonalDataInfoEdit>();

  const tabTranslation = useSelector(personalDataTranslationSelector());

  const [onChangeAddressTimeout, setChangeAddressTimeout] = useState(0);
  const [search, setSearch] = useState('');

  const { data, isFetching } = useSearchPersonalDataNationalityQuery(search, {
    skip: !search,
  });

  if (!isPersonalDataNationalInfoRequired(docTypeSelected)) return null;

  return (
    <div className="edit-form__national-info">
      <FormField
        required
        dataName="edit-personal-data-national-info"
        label={tabTranslation.personalData.fields.nationalityInfo}
        name="personalData.nationalityInfo"
      >
        {({ field, label, kind, dataName, required }) => {
          const onChangeHandler = (value: string) => {
            setFieldValue(field.name, { def: value }, false);
            setFieldTouched(field.name, true, false);
            if (onChangeAddressTimeout > 0) {
              clearTimeout(onChangeAddressTimeout);
            }
            setChangeAddressTimeout(
              window.setTimeout(() => {
                setSearch(value);
                validateField(field.name);
              }, DEBOUNCE_DEFAULT_DELAY),
            );
          };

          const onSelectHandler = (value: IInputSearchItem) => {
            setFieldValue(field.name, { def: value.value, id: value.id });
          };

          return (
            <InputSearch
              foundItems={data}
              onChange={onChangeHandler}
              onSelect={onSelectHandler}
            >
              <Input
                dataName={dataName}
                kind={kind}
                label={label}
                required={required}
                righticon={
                  isFetching ? icons.searchIndicator : icons.searchSmallIcon
                }
                value={field.value?.def || ''}
                onBlur={() => setFieldTouched(field.name, true)}
              />
            </InputSearch>
          );
        }}
      </FormField>
    </div>
  );
};

export default PersonalDataEditNationality;
